<template>
  <div class="media-page page-ind-analitica min-h-screen w-full p-3 md:p-4">

    <!-- ── Toolbar ── -->
    <section class="ind-toolbar mb-4">
      <div class="ind-toolbar-left">
        <div class="ind-toolbar-field">
          <label class="field-label block mb-1">Desde</label>
          <Calendar
            v-model="fechaDesde"
            view="month"
            dateFormat="mm/yy"
            class="ind-month-picker"
            :maxDate="fechaHasta"
            @date-select="onRangeChange"
          />
        </div>
        <div class="ind-toolbar-field">
          <label class="field-label block mb-1">Hasta</label>
          <Calendar
            v-model="fechaHasta"
            view="month"
            dateFormat="mm/yy"
            class="ind-month-picker"
            :minDate="fechaDesde"
            :maxDate="fechaMax"
            @date-select="onRangeChange"
          />
        </div>
        <div class="ind-toolbar-actions">
          <Button
            label="12 meses"
            icon="pi pi-calendar"
            class="ind-range-btn"
            :class="{ 'ind-range-btn-active': rangoPreset === '12m' }"
            @click="presetRange('12m')"
          />
          <Button
            label="24 meses"
            icon="pi pi-calendar"
            class="ind-range-btn"
            :class="{ 'ind-range-btn-active': rangoPreset === '24m' }"
            @click="presetRange('24m')"
          />
          <Button
            label="36 meses"
            icon="pi pi-calendar"
            class="ind-range-btn"
            :class="{ 'ind-range-btn-active': rangoPreset === '36m' }"
            @click="presetRange('36m')"
          />
        </div>
      </div>
      <div class="ind-toolbar-right">
        <Button
          label="Actualizar"
          icon="pi pi-refresh"
          :loading="loading"
          class="p-button-sm p-button-outlined"
          @click="cargarDatos"
        />
      </div>
    </section>

    <!-- ── Banner: mes en curso ── -->
    <div v-if="mesEnCursoIncluido" class="analitica-aviso-box mb-3">
      <i class="pi pi-info-circle mr-2"></i>
      El rango incluye el mes en curso (datos parciales).
      Los valores del mes actual no son comparables con meses completos.
    </div>

    <!-- ── Error ── -->
    <div v-if="error" class="kpi-error-box mb-5">
      <i class="pi pi-exclamation-triangle mr-2"></i>{{ error }}
    </div>

    <!-- ── Skeleton carga inicial ── -->
    <template v-if="loading && !series.length">
      <div class="analitica-skel-page">
        <KpiSectionSkeleton variant="hero" />
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5" style="margin-top:20px">
          <KpiSectionSkeleton variant="hero" />
          <KpiSectionSkeleton variant="hero" />
        </div>
        <KpiSectionSkeleton variant="hero" style="margin-top:20px" />
        <KpiSectionSkeleton variant="strip" :itemCount="3" style="margin-top:20px" />
        <KpiSectionSkeleton variant="hero" style="margin-top:20px" />
        <KpiSectionSkeleton variant="strip" :itemCount="4" style="margin-top:20px" />
        <KpiSectionSkeleton variant="table" :itemCount="5" style="margin-top:20px" />
      </div>
    </template>

    <!-- ── Contenido ── -->
    <template v-else-if="series.length">

      <!-- ── Quick Nav ── -->
      <nav class="analitica-quick-nav mb-5" aria-label="Navegacion rapida">
        <button type="button" class="analitica-quick-nav-btn" data-section="spots"
                @click="scrollToSection('section-spots')">
          <i class="pi pi-megaphone" style="font-size:0.7rem;color:#3b82f6"></i>
          <span>Spots</span>
        </button>
        <button type="button" class="analitica-quick-nav-btn" data-section="humanos"
                @click="scrollToSection('section-humanos')">
          <i class="pi pi-user" style="font-size:0.7rem;color:#10b981"></i>
          <span>Humanos</span>
        </button>
        <button type="button" class="analitica-quick-nav-btn" data-section="ia"
                @click="scrollToSection('section-ia')">
          <i class="pi pi-robot" style="font-size:0.7rem;color:#8b5cf6"></i>
          <span>IA</span>
        </button>
        <button type="button" class="analitica-quick-nav-btn" data-section="comparativa"
                @click="scrollToSection('section-comparativa')">
          <i class="pi pi-calendar" style="font-size:0.7rem;color:#6366f1"></i>
          <span>Comparativa</span>
        </button>
        <button type="button" class="analitica-quick-nav-btn" data-section="conectados"
                @click="scrollToSection('section-conectados')">
          <i class="pi pi-chart-line" style="font-size:0.7rem;color:#10b981"></i>
          <span>Conectados</span>
        </button>
        <button type="button" class="analitica-quick-nav-btn" data-section="musicalizacion"
                @click="scrollToSection('section-musicalizacion')">
          <i class="pi pi-volume-up" style="font-size:0.7rem;color:#06b6d4"></i>
          <span>Musicalizacion</span>
        </button>
        <button type="button" class="analitica-quick-nav-btn" data-section="canales"
                @click="scrollToSection('section-canales')">
          <i class="pi pi-sync" style="font-size:0.7rem;color:#f97316"></i>
          <span>Canales</span>
        </button>
        <button type="button" class="analitica-quick-nav-btn" data-section="por-marca"
                @click="scrollToSection('section-por-marca')">
          <i class="pi pi-history" style="font-size:0.7rem;color:#ef4444"></i>
          <span>Por Marca</span>
        </button>
      </nav>

      <div class="analitica-page-content" :class="{ 'analitica-sections-refreshing': isRefreshing }">

        <!-- ═══ 1: SPOTS GLOBALES ═══ -->
        <section id="section-spots" class="kpi-section mb-5"
          :class="{ 'kpi-section--loading': sectionLoading }">
          <h3 class="kpi-section-title" @click="toggleSeccion('spots')">
            <i class="pi pi-megaphone kpi-section-icon" style="font-size:0.92rem;color:#3b82f6"></i>
            Spots Globales vs Meta
            <span v-if="sectionLoading" class="kpi-loading-dot"></span>
            <button type="button" class="kpi-collapse-toggle"
                    @click.stop="toggleSeccion('spots')"
                    :aria-label="collapsed.spots ? 'Expandir' : 'Colapsar'">
              <i class="pi pi-chevron-up" :class="{ 'kpi-chevron-collapsed': collapsed.spots }"></i>
            </button>
          </h3>
          <transition name="kpi-collapse">
            <div v-show="!collapsed.spots" class="kpi-section-body">
              <!-- Hero card -->
              <div class="kpi-mus-hero mb-3" :class="heroClassSpots">
                <div class="kpi-mus-hero-row" style="justify-content:center">
                  <div class="kpi-mus-hero-left" style="text-align:center;width:100%">
                    <span class="kpi-card-label">Spots producidos ultimo mes</span>
                    <div class="kpi-mus-hero-pct">{{ ultimoLogrados?.toLocaleString() || '—' }}</div>
                    <span class="kpi-card-sub">
                      Meta: {{ ultimoMeta?.toLocaleString() || '—' }}
                      &middot; {{ ultimoCumple ? 'Meta alcanzada' : 'Meta no alcanzada' }}
                    </span>
                  </div>
                </div>
              </div>
              <Chart type="bar" :data="chartProduccion" :options="chartOptionsProduccion" class="analitica-chart-render" />
              <div class="analitica-insight"><i class="pi pi-info-circle"></i> {{ insightProduccion }}</div>
            </div>
          </transition>
        </section>

        <!-- ═══ 2+3: SPOTS HUMANOS + IA ═══ -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
          <section id="section-humanos" class="kpi-section"
            :class="{ 'kpi-section--loading': sectionLoading }">
            <h3 class="kpi-section-title" @click="toggleSeccion('humanos')">
              <i class="pi pi-user kpi-section-icon" style="font-size:0.92rem;color:#10b981"></i>
              Spots Humanos vs Meta
              <button type="button" class="kpi-collapse-toggle"
                      @click.stop="toggleSeccion('humanos')">
                <i class="pi pi-chevron-up" :class="{ 'kpi-chevron-collapsed': collapsed.humanos }"></i>
              </button>
            </h3>
            <transition name="kpi-collapse">
              <div v-show="!collapsed.humanos" class="kpi-section-body">
                <Chart type="bar" :data="chartSpotsHumanos" :options="chartOptionsDefault" class="analitica-chart-render" />
                <div class="analitica-insight"><i class="pi pi-info-circle"></i> {{ insightSpotsHumanos }}</div>
              </div>
            </transition>
          </section>

          <section id="section-ia" class="kpi-section"
            :class="{ 'kpi-section--loading': sectionLoading }">
            <h3 class="kpi-section-title" @click="toggleSeccion('ia')">
              <i class="pi pi-robot kpi-section-icon" style="font-size:0.92rem;color:#8b5cf6"></i>
              Spots IA vs Meta
              <button type="button" class="kpi-collapse-toggle"
                      @click.stop="toggleSeccion('ia')">
                <i class="pi pi-chevron-up" :class="{ 'kpi-chevron-collapsed': collapsed.ia }"></i>
              </button>
            </h3>
            <transition name="kpi-collapse">
              <div v-show="!collapsed.ia" class="kpi-section-body">
                <Chart type="bar" :data="chartSpotsIa" :options="chartOptionsDefault" class="analitica-chart-render" />
                <div class="analitica-insight"><i class="pi pi-info-circle"></i> {{ insightSpotsIa }}</div>
              </div>
            </transition>
          </section>
        </div>

        <!-- ═══ 4: COMPARATIVA ANUAL ═══ -->
        <section id="section-comparativa" class="kpi-section mb-5"
          :class="{ 'kpi-section--loading': sectionLoading }">
          <h3 class="kpi-section-title" @click="toggleSeccion('comparativa')">
            <i class="pi pi-calendar kpi-section-icon" style="font-size:0.92rem;color:#6366f1"></i>
            {{ comparativaAnterior?.year || '—' }} vs {{ comparativaActual?.year || '—' }}
            <span class="analitica-chart-subtitle">Año anterior vs año actual</span>
            <span v-if="comparativaDifPct != null" :class="comparativaDifPct >= 0 ? 'analitica-diff--pos' : 'analitica-diff--neg'" class="analitica-comparativa-diff">
              {{ comparativaDifPct >= 0 ? '+' : '' }}{{ comparativaDifPct }}%
            </span>
            <button type="button" class="kpi-collapse-toggle"
                    @click.stop="toggleSeccion('comparativa')">
              <i class="pi pi-chevron-up" :class="{ 'kpi-chevron-collapsed': collapsed.comparativa }"></i>
            </button>
          </h3>
          <transition name="kpi-collapse">
            <div v-show="!collapsed.comparativa" class="kpi-section-body">
              <div v-if="comparativaError" class="analitica-section-error">{{ comparativaError }}</div>
              <Chart v-else type="bar" :data="chartComparativa" :options="chartOptionsDefault" class="analitica-chart-render" />
            </div>
          </transition>
        </section>

        <!-- ═══ 5: CONECTADOS ═══ -->
        <section id="section-conectados" class="kpi-section mb-5"
          :class="{ 'kpi-section--loading': sectionLoading }">
          <h3 class="kpi-section-title" @click="toggleSeccion('conectados')">
            <i class="pi pi-chart-line kpi-section-icon" style="font-size:0.92rem;color:#10b981"></i>
            Conectados
            <span v-if="sectionLoading" class="kpi-loading-dot"></span>
            <button type="button" class="kpi-collapse-toggle"
                    @click.stop="toggleSeccion('conectados')">
              <i class="pi pi-chevron-up" :class="{ 'kpi-chevron-collapsed': collapsed.conectados }"></i>
            </button>
          </h3>
          <transition name="kpi-collapse">
            <div v-show="!collapsed.conectados" class="kpi-section-body">
              <div class="kpi-mus-hero mb-3" :class="heroClassConectados">
                <div class="kpi-mus-hero-row" style="justify-content:center">
                  <div class="kpi-mus-hero-left" style="text-align:center;width:100%">
                    <span class="kpi-card-label">Conectados ultimo mes</span>
                    <div class="kpi-mus-hero-pct">{{ ultimoConectados?.toLocaleString() || '—' }}</div>
                    <span class="analitica-badge legacy" v-if="ultimoConectadosLegacy">legacy</span>
                    <span class="kpi-card-sub" v-if="ultimoCapacidad">
                      de {{ ultimoCapacidad.toLocaleString() }} sucursales
                    </span>
                  </div>
                </div>
              </div>
              <Chart type="line" :data="chartOcupacion" :options="chartOptionsOcupacion" class="analitica-chart-render" />
              <div class="analitica-insight"><i class="pi pi-info-circle"></i> {{ insightOcupacion }}</div>
            </div>
          </transition>
        </section>

        <!-- ═══ 6: MUSICALIZACIÓN ═══ -->
        <section id="section-musicalizacion" class="kpi-section mb-5"
          :class="{ 'kpi-section--loading': sectionLoading }">
          <h3 class="kpi-section-title" @click="toggleSeccion('musicalizacion')">
            <i class="pi pi-volume-up kpi-section-icon" style="font-size:0.92rem;color:#06b6d4"></i>
            Musicalización
            <span class="analitica-chart-subtitle">Canales actualizados vs totales</span>
            <span v-if="musicalizacionSerieLoading" class="kpi-loading-dot"></span>
            <button type="button" class="kpi-collapse-toggle"
                    @click.stop="toggleSeccion('musicalizacion')">
              <i class="pi pi-chevron-up" :class="{ 'kpi-chevron-collapsed': collapsed.musicalizacion }"></i>
            </button>
          </h3>
          <transition name="kpi-collapse">
            <div v-show="!collapsed.musicalizacion" class="kpi-section-body">
              <div v-if="musicalizacionSerieLoading" class="analitica-section-loading">Cargando...</div>
              <div v-else-if="musicalizacionSerieError" class="analitica-section-error">{{ musicalizacionSerieError }}</div>
              <template v-else-if="musicalizacionSerieLoaded">
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  <!-- Canales musicales -->
                  <div>
                    <div class="kpi-mus-hero mb-3" style="background:rgba(6,182,212,0.06);border-color:rgba(6,182,212,0.18)">
                      <div class="kpi-mus-hero-row" style="justify-content:center">
                        <div class="kpi-mus-hero-left" style="text-align:center;width:100%">
                          <span class="kpi-card-label">Canales musicales — último mes</span>
                          <div class="kpi-mus-hero-pct" style="color:#06b6d4">{{ insightMusicalMusical }}</div>
                        </div>
                      </div>
                    </div>
                    <Chart type="line" :data="chartMusicalizacionMusical" :options="chartOptionsMusicalizacion" class="analitica-chart-render" />
                  </div>
                  <!-- Canales video -->
                  <div>
                    <div class="kpi-mus-hero mb-3" style="background:rgba(139,92,246,0.06);border-color:rgba(139,92,246,0.18)">
                      <div class="kpi-mus-hero-row" style="justify-content:center">
                        <div class="kpi-mus-hero-left" style="text-align:center;width:100%">
                          <span class="kpi-card-label">Canales video — último mes</span>
                          <div class="kpi-mus-hero-pct" style="color:#8b5cf6">{{ insightMusicalVideo }}</div>
                        </div>
                      </div>
                    </div>
                    <Chart type="line" :data="chartMusicalizacionVideo" :options="chartOptionsMusicalizacion" class="analitica-chart-render" />
                  </div>
                </div>
              </template>
              <div v-else class="analitica-section-empty">Sin datos</div>
            </div>
          </transition>
        </section>

        <!-- ═══ 6b: INTEGRIDAD BIBLIOTECA ═══ -->
        <section id="section-integridad" class="kpi-section mb-5"
          :class="{ 'kpi-section--loading': sectionLoading }">
          <h3 class="kpi-section-title" @click="toggleSeccion('integridad')">
            <i class="pi pi-exclamation-triangle kpi-section-icon" style="font-size:0.92rem;color:#f59e0b"></i>
            Integridad de la biblioteca
            <span class="analitica-chart-subtitle">Canciones repetidas y sin tag por mes</span>
            <span v-if="integridadSerieLoading" class="kpi-loading-dot"></span>
            <button type="button" class="kpi-collapse-toggle"
                    @click.stop="toggleSeccion('integridad')">
              <i class="pi pi-chevron-up" :class="{ 'kpi-chevron-collapsed': collapsed.integridad }"></i>
            </button>
          </h3>
          <transition name="kpi-collapse">
            <div v-show="!collapsed.integridad" class="kpi-section-body">
              <div v-if="integridadSerieLoading" class="analitica-section-loading">Cargando...</div>
              <div v-else-if="integridadSerieError" class="analitica-section-error">{{ integridadSerieError }}</div>
              <template v-else-if="integridadSerieLoaded">
                <div class="kpi-mus-hero mb-3" style="background:rgba(245,158,11,0.06);border-color:rgba(245,158,11,0.18)">
                  <div class="kpi-mus-hero-row" style="justify-content:center">
                    <div class="kpi-mus-hero-left" style="text-align:center;width:100%">
                      <span class="kpi-card-label">Integridad — último mes</span>
                      <div class="kpi-mus-hero-pct" style="color:#f59e0b">{{ insightIntegridad }}</div>
                    </div>
                  </div>
                </div>
                <Chart type="line" :data="chartIntegridad" :options="chartOptionsIntegridad" class="analitica-chart-render" />
              </template>
              <div v-else class="analitica-section-empty">Sin datos</div>
            </div>
          </transition>
        </section>

        <!-- ═══ 7: HISTORIAL CANALES ═══ -->
        <section id="section-canales" class="kpi-section mb-5"
          :class="{ 'kpi-section--loading': sectionLoading }">
          <h3 class="kpi-section-title" @click="toggleSeccion('canales')">
            <i class="pi pi-sync kpi-section-icon" style="font-size:0.92rem;color:#f97316"></i>
            Altas, Bajas y Cambios de Canales
            <div class="analitica-historial-badges">
              <span class="analitica-badge alta">+{{ historialTotales?.altas || 0 }}</span>
              <span class="analitica-badge baja">−{{ historialTotales?.bajas || 0 }}</span>
              <span class="analitica-badge deshabilitacion">⊘{{ historialTotales?.deshabilitaciones || 0 }}</span>
              <span class="analitica-badge reactivacion">↻{{ historialTotales?.reactivaciones || 0 }}</span>
              <span class="analitica-badge cambio">~{{ historialTotales?.cambios || 0 }}</span>
            </div>
            <button type="button" class="kpi-collapse-toggle"
                    @click.stop="toggleSeccion('canales')">
              <i class="pi pi-chevron-up" :class="{ 'kpi-chevron-collapsed': collapsed.canales }"></i>
            </button>
          </h3>
          <transition name="kpi-collapse">
            <div v-show="!collapsed.canales" class="kpi-section-body">
              <div v-if="historialError" class="analitica-section-error">{{ historialError }}</div>
              <DataTable v-else-if="historialCanales.length" :value="historialCanales" responsiveLayout="scroll" stripedRows class="text-sm" :paginator="true" :rows="10">
                <Column field="radio_name" header="Canal" sortable style="min-width:120px" />
                <Column field="accion" header="Acción" sortable style="width:120px">
                  <template #body="{ data: row }">
                    <Badge :value="
                      row.accion === 'alta' ? 'Alta' :
                      row.accion === 'baja' ? 'Baja' :
                      row.accion === 'deshabilitacion' ? 'Deshabilitación' :
                      row.accion === 'reactivacion' ? 'Reactivación' :
                      row.accion === 'cambio' ? 'Cambio' : row.accion
                    " :severity="
                      row.accion === 'alta' ? 'success' :
                      row.accion === 'baja' ? 'danger' :
                      row.accion === 'deshabilitacion' ? 'warn' :
                      row.accion === 'reactivacion' ? 'info' :
                      'warning'" />
                  </template>
                </Column>
                <Column field="detalle" header="Detalle" style="min-width:200px" />
                <Column field="usuario_name" header="Cambió por" sortable style="width:120px">
                  <template #body="{ data: row }">
                    <span>{{ row.usuario_name || '—' }}</span>
                  </template>
                </Column>
                <Column field="fecha_evento" header="Fecha" sortable style="width:120px" />
              </DataTable>
              <div v-if="!historialError && !historialCanales.length" class="analitica-section-empty">Sin movimientos de canales en el periodo</div>
            </div>
          </transition>
        </section>

        <!-- ═══ 8: HISTORICO POR CANAL (TABLA MULTI-CANAL) ═══ -->
        <section id="section-por-marca" class="kpi-section mb-5">
          <h3 class="kpi-section-title" @click="toggleSeccion('porMarca')">
            <i class="pi pi-history kpi-section-icon" style="font-size:0.92rem;color:#ef4444;opacity:0.85"></i>
            Histórico por Canal
            <span v-if="porMarcaLoading" class="kpi-loading-dot" title="Cargando..."></span>
            <button type="button" class="kpi-collapse-toggle"
                    @click.stop="toggleSeccion('porMarca')"
                    :aria-label="collapsed.porMarca ? 'Expandir sección' : 'Colapsar sección'">
              <i class="pi pi-chevron-up" :class="{ 'kpi-chevron-collapsed': collapsed.porMarca }"></i>
            </button>
          </h3>
          <transition name="kpi-collapse">
            <div v-show="!collapsed.porMarca" class="kpi-section-body">

              <!-- Controles: MultiSelect + botón Consultar -->
              <div class="hist-canal-controls mb-3">
                <div class="hist-canal-selector">
                  <i class="pi pi-search"></i>
                  <MultiSelect
                    v-model="porMarcaCanalesSeleccionados"
                    :options="radiosOptions"
                    optionLabel="label"
                    optionValue="value"
                    :placeholder="radiosOptions.length ? 'Selecciona canales para comparar...' : 'Cargando canales...'"
                    display="chip"
                    filter
                    :filterPlaceholder="'Buscar canal...'"
                    class="hist-canal-multiselect"
                    :loading="radiosOptions.length === 0"
                    :maxSelectedLabels="3"
                    :showSelectAll="true"
                    selectAllLabel="Todos"
                  />
                </div>
                <div class="hist-canal-actions">
                  <Button
                    :label="porMarcaBotonLabel"
                    icon="pi pi-search"
                    :loading="porMarcaLoading"
                    :disabled="porMarcaBotonDisabled"
                    class="p-button-sm"
                    :class="{'hist-canal-btn-pending': porMarcaHayCambios && !porMarcaLoading}"
                    @click="cargarPorMarcaMulti"
                  />
                  <span v-if="porMarcaCambiosLabel" class="hist-canal-pending-badge">
                    <i class="pi pi-exclamation-circle"></i>
                    {{ porMarcaCambiosLabel }}
                  </span>
                  <span v-else-if="porMarcaTimestamp" class="hist-canal-last-ok">
                    <i class="pi pi-check-circle"></i>
                    Última consulta: {{ porMarcaTimestampLabel }} · {{ porMarcaMultiData.length }} {{ porMarcaMultiData.length === 1 ? 'canal' : 'canales' }}
                  </span>
                </div>
                <!-- Export toolbar -->
                <div v-if="porMarcaTablaFilas.length && porMarcaMesesConsultados" class="hist-canal-export">
                  <Button
                    icon="pi pi-copy"
                    class="p-button-text p-button-sm hist-canal-export-btn export-copy"
                    v-tooltip.top="'Copiar imagen'"
                    :loading="exportLoading"
                    @click="copiarPNG"
                  />
                  <Button
                    icon="pi pi-image"
                    class="p-button-text p-button-sm hist-canal-export-btn export-png"
                    v-tooltip.top="'Exportar PNG'"
                    :loading="exportLoading"
                    @click="exportarPNG"
                  />
                  <Button
                    icon="pi pi-file-excel"
                    class="p-button-text p-button-sm hist-canal-export-btn export-xlsx"
                    v-tooltip.top="'Exportar XLSX'"
                    @click="exportarXLSX"
                  />
                  <Button
                    icon="pi pi-file-pdf"
                    class="p-button-text p-button-sm hist-canal-export-btn export-pdf"
                    v-tooltip.top="'Exportar PDF'"
                    @click="exportarPDF"
                  />
                </div>
              </div>
              <div v-if="!porMarcaCanalesConsultados && !porMarcaLoading" class="hist-canal-empty">
                <i class="pi pi-search hist-canal-empty-icon"></i>
                <p class="hist-canal-empty-title">Selecciona 1 o más canales para comparar</p>
                <p class="hist-canal-empty-hint">Compara producciones humanas, con IA y metas mes a mes</p>
              </div>

              <!-- Error -->
              <div v-if="porMarcaError" class="hist-canal-error mb-3">{{ porMarcaError }}</div>

              <!-- Tabla comparativa -->
              <DataTable
                v-if="porMarcaTablaFilas.length && porMarcaMesesConsultados"
                :value="porMarcaTablaFilas"
                scrollable
                scrollDirection="both"
                scrollHeight="flex"
                class="hist-canal-table p-datatable-sm"
                stripedRows
                :frozenValue="[]"
              >
                <Column field="name" header="Canal" frozen style="min-width:240px;width:240px">
                  <template #body="{ data: row }">
                    <div class="hist-canal-name">
                      <i class="pi" :class="row.kind === 'client' ? 'pi-building' : 'pi-signal'" :style="{ color: row.kind === 'client' ? '#f59e0b' : '#64748b' }"></i>
                      <span>{{ row.name }}</span>
                    </div>
                  </template>
                </Column>
                <Column
                  v-for="mes in porMarcaMesesConsultados"
                  :key="mes"
                  :field="mes"
                  :header="formatMesCorto(mes)"
                  style="min-width:130px;width:130px"
                >
                  <template #body="{ data: row }">
                    <HistCanalCell v-if="row.porMes[mes]" :data="row.porMes[mes]" />
                    <div v-else class="hist-cell-no-data">—</div>
                  </template>
                </Column>
                <Column field="total" header="Total" frozen alignFrozen="right" style="min-width:150px;width:150px">
                  <template #body="{ data: row }">
                    <HistCanalCell :data="row.totales" :isTotal="true" />
                  </template>
                </Column>
              </DataTable>

              <!-- Tabla espejo offscreen para exportación PNG -->
              <div
                v-if="porMarcaTablaFilas.length && porMarcaMesesConsultados"
                ref="porMarcaExportNode"
                class="por-marca-export-wrap"
              >
                <table class="por-marca-export-table" :style="{ width: (200 + porMarcaMesesConsultados.length * 130 + 140) + 'px' }">
                  <colgroup>
                    <col style="width:200px">
                    <col v-for="mes in porMarcaMesesConsultados" :key="'col-'+mes" style="width:130px">
                    <col style="width:140px">
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Canal</th>
                      <th v-for="mes in porMarcaMesesConsultados" :key="'exp-'+mes">{{ formatMesCorto(mes) }}</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in porMarcaTablaFilas" :key="'exp-'+row.id">
                      <td class="por-marca-export-td-canal">
                        <i class="pi" :class="row.kind === 'client' ? 'pi-building' : 'pi-signal'" :style="{ color: row.kind === 'client' ? '#f59e0b' : '#64748b', fontSize: '0.85rem' }"></i>
                        <span>{{ row.name }}</span>
                      </td>
                      <td v-for="mes in porMarcaMesesConsultados" :key="'exp-'+row.id+'-'+mes" class="por-marca-export-td-cell">
                        <HistCanalCell v-if="row.porMes[mes]" :data="row.porMes[mes]" />
                        <span v-else class="hist-cell-no-data">—</span>
                      </td>
                      <td class="por-marca-export-td-cell">
                        <HistCanalCell :data="row.totales" :isTotal="true" />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

            </div>
          </transition>
        </section>

      </div>
    </template>

    <!-- ── Sin datos ── -->
    <div v-else-if="!loading && !error" class="analitica-empty">
      <i class="pi pi-chart-line analitica-empty-icon"></i>
      <p class="analitica-empty-title">Sin datos para el rango seleccionado</p>
      <p class="analitica-empty-hint">Selecciona un rango de meses con datos disponibles.</p>
    </div>

    <!-- Export overlay -->
    <div v-if="exportLoading" class="export-overlay">
      <div class="export-overlay-card">
        <i class="pi pi-spin pi-spinner export-overlay-icon"></i>
        <span class="export-overlay-text">Generando imagen…</span>
      </div>
    </div>

  </div>
</template>

<script>
import axiosInstance from '@/axiosInstance';
import KpiSectionSkeleton from '@/components/KpiSectionSkeleton.vue';
import HistCanalCell from '@/components/HistCanalCell.vue';
import MultiSelect from 'primevue/multiselect';
import { toCanvas } from 'html-to-image';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';

export default {
  name: 'IndicadoresOpAnalitica',
  components: { KpiSectionSkeleton, HistCanalCell, MultiSelect },

  data() {
    const now = new Date();
    // Último mes completo (mes anterior al actual)
    const ultimoCompleto = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const doceAtras = new Date(ultimoCompleto.getFullYear(), ultimoCompleto.getMonth() - 11, 1);
    return {
      fechaDesde: doceAtras,
      fechaHasta: ultimoCompleto,
      fechaMax: now,
      rangoPreset: '12m',
      loading: false,
      error: null,
      series: [],
      // 7c: Comparativa anual
      comparativaActual: null,
      comparativaAnterior: null,
      comparativaDifPct: null,
      // 7e: Historial canales
      historialCanales: [],
      // 7f: Musicalizacion serie
      musicalizacionSerie: [],
      musicalizacionSerieLoading: false,
      musicalizacionSerieError: null,
      musicalizacionCurrentMonth: null,
      // Integridad de la biblioteca serie
      integridadSerie: [],
      integridadSerieLoading: false,
      integridadSerieError: null,
      integridadCurrentMonth: null,
      isDarkNow: false,
      comparativaError: null,
      historialError: null,
      debounceTimer: null,
      abortController: null,
      collapsed: { spots: false, humanos: false, ia: false, comparativa: false, conectados: false, musicalizacion: false, integridad: false, canales: false, porMarca: false },
      sectionLoading: false,
      isRefreshing: false,
      // Historico por marca (tabla multi-canal)
      radiosOptions: [],
      porMarcaCanalesSeleccionados: [],
      porMarcaCanalesConsultados: null,   // null | array ordenado de valores
      porMarcaMesesConsultados: null,     // null | array ordenado de 'YYYY-MM'
      porMarcaMultiData: [],              // [{kind, id, name, stream, series: [...]}]
      porMarcaTimestamp: null,            // Date() de la última consulta
      porMarcaLoading: false,
      porMarcaError: null,
      exportLoading: false,
    };
  },

  computed: {

    /* ── Labels meses (ej: "Ene 2025") ── */
    labels() {
      const months = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
      const ahora = new Date();
      const mesActual = `${ahora.getFullYear()}-${String(ahora.getMonth() + 1).padStart(2, '0')}`;
      return this.series.map((s, i) => {
        const [y, m] = s.mes.split('-');
        let lbl = months[parseInt(m) - 1] + ' ' + y;
        // Agregar indicador si es el último mes y es el actual
        if (i === this.series.length - 1 && s.mes === mesActual) {
          lbl += ' ⚠️';
        }
        return lbl;
      });
    },

    /* ── Datasets base ── */

    // Conectados: promedio diario de dispositivos
    ocupacionSerie() {
      return this.conectadosPromedio;
    },

    // Spots logrados y meta
    spotsLogrados() {
      return this.series.map(s => s.global?.spots_logrados ?? 0);
    },
    spotsMeta() {
      return this.series.map(s => { const m = s.global?.meta_spots ?? 0; return m > 0 ? m : null; });
    },
    spotsCumplen() {
      return this.series.map((s, i) => {
        if (this.spotsMeta[i] == null) return null;
        return this.spotsLogrados[i] >= this.spotsMeta[i];
      });
    },

    // IA: % del total y eficiencia
    spotsIaLogrados() {
      return this.series.map(s => s.global?.spots_ia_logrados ?? 0);
    },
    spotsHumanos() {
      return this.spotsLogrados.map((v, i) => Math.max(0, v - (this.spotsIaLogrados[i] || 0)));
    },

    // ── Helper: último mes ──
    ultimo() {
      return this.series.length ? this.series[this.series.length - 1] : null;
    },

    // ── Histórico por Canal: estado multi-canal ──
    porMarcaBotonDisabled() {
      if (this.porMarcaLoading) return true;
      if (!this.porMarcaCanalesSeleccionados.length) return true;
      return !this.porMarcaHayCambios;
    },
    porMarcaHayCambios() {
      if (!this.porMarcaCanalesConsultados) return this.porMarcaCanalesSeleccionados.length > 0;
      const sel = this._canalesKey(this.porMarcaCanalesSeleccionados);
      const con = this._canalesKey(this.porMarcaCanalesConsultados);
      if (sel !== con) return true;
      const mSel = this._generarMeses(this.fechaDesde, this.fechaHasta).join(',');
      const mCon = (this.porMarcaMesesConsultados || []).join(',');
      return mSel !== mCon;
    },
    porMarcaBotonLabel() {
      if (this.porMarcaLoading) return 'Consultando...';
      const n = this.porMarcaCanalesSeleccionados.length;
      if (n === 0) return 'Consultar';
      return `Consultar ${n} ${n === 1 ? 'canal' : 'canales'}`;
    },
    porMarcaCambiosLabel() {
      if (this.porMarcaLoading) return '';
      if (!this.porMarcaCanalesConsultados) {
        if (this.porMarcaCanalesSeleccionados.length) {
          return `${this.porMarcaCanalesSeleccionados.length} ${this.porMarcaCanalesSeleccionados.length === 1 ? 'canal seleccionado' : 'canales seleccionados'} sin consultar`;
        }
        return '';
      }
      const selCambio = this._canalesKey(this.porMarcaCanalesSeleccionados) !== this._canalesKey(this.porMarcaCanalesConsultados);
      const mCambio = this._generarMeses(this.fechaDesde, this.fechaHasta).join(',') !== (this.porMarcaMesesConsultados || []).join(',');
      if (selCambio && mCambio) return 'Selección y rango modificados';
      if (selCambio) return 'Selección modificada';
      if (mCambio) return 'Rango modificado';
      return '';
    },
    porMarcaTimestampLabel() {
      if (!this.porMarcaTimestamp) return '';
      const d = this.porMarcaTimestamp;
      const hh = String(d.getHours()).padStart(2, '0');
      const mm = String(d.getMinutes()).padStart(2, '0');
      return `${hh}:${mm}`;
    },
    porMarcaTablaFilas() {
      return this.porMarcaMultiData.map(canal => {
        const tot = canal.series.reduce((acc, m) => ({
          humanos: acc.humanos + (Number(m.spots_humanos) || 0),
          ia: acc.ia + (Number(m.spots_ia) || 0),
          metaHumano: acc.metaHumano + (Number(m.meta_humano) || 0),
          metaIa: acc.metaIa + (Number(m.meta_ia) || 0),
        }), { humanos: 0, ia: 0, metaHumano: 0, metaIa: 0 });
        return {
          id: canal.id,
          name: canal.name,
          kind: canal.kind,
          totales: tot,
          porMes: Object.fromEntries(canal.series.map(m => [m.mes, m])),
        };
      });
    },

    // ── Conectados ──
    conectadosPromedio() {
      return this.series.map(s => s.conectados?.promedio_diario ?? null);
    },
    conectadosCapacidad() {
      return this.series.map(s => s.conectados?.total_sucursales ?? null);
    },
    conectadosDias() {
      return this.series.map(s => s.conectados?.dias_con_datos ?? 0);
    },
    conectadosTotalDias() {
      return this.series.map(s => s.conectados?.total_dias_mes ?? 0);
    },

    /* ── Hero card values ── */
    ultimoLogrados() { return this.ultimo?.global?.spots_logrados ?? null; },
    ultimoMeta() { return this.ultimo?.global?.meta_spots ?? null; },
    ultimoCumple() { return this.ultimoLogrados != null && this.ultimoMeta != null ? this.ultimoLogrados >= this.ultimoMeta : null; },
    heroClassSpots() {
      if (this.ultimoCumple === null) return 'kpi-mus-hero-neutral';
      return this.ultimoCumple ? 'kpi-mus-hero-green' : 'kpi-mus-hero-red';
    },

    /* ── Hero card: Conectados ── */
    ultimoConectados() { return this.conectadosPromedio.length ? this.conectadosPromedio[this.conectadosPromedio.length - 1] : null; },
    ultimoCapacidad() { return this.conectadosCapacidad.length ? this.conectadosCapacidad[this.conectadosCapacidad.length - 1] : null; },
    ultimoConectadosLegacy() { return this.ultimo?.conectados?.legacy ?? true; },
    conectadosLegacyMap() { return this.series.map(s => !!s.conectados?.legacy); },
    conectadosPointColors() {
      return this.conectadosLegacyMap.map(l => l ? '#f59e0b' : '#10b981');
    },
    heroClassConectados() {
      if (this.ultimoConectados === null || !this.ultimoCapacidad) return 'kpi-mus-hero-neutral';
      const ratio = this.ultimoConectados / this.ultimoCapacidad;
      if (ratio >= 0.9) return 'kpi-mus-hero-green';
      if (ratio >= 0.7) return 'kpi-mus-hero-yellow';
      return 'kpi-mus-hero-red';
    },

    // ── Conectados ──
    mesEnCursoIncluido() {
      if (!this.series.length) return false;
      const ultimoMes = this.series[this.series.length - 1].mes;
      const ahora = new Date();
      const mesActual = `${ahora.getFullYear()}-${String(ahora.getMonth() + 1).padStart(2, '0')}`;
      return ultimoMes === mesActual;
    },

    /* ── Insights automáticos ── */

    insightOcupacion() {
      const n = this.conectadosPromedio.length;
      if (!n) return 'Sin datos suficientes para análisis.';
      const actual = this.conectadosPromedio[n - 1];
      const cap = this.conectadosCapacidad[n - 1];
      if (actual === null) return 'Sin datos de conectados en el último mes.';
      const valids = this.conectadosPromedio.filter(v => v !== null);
      const prom = valids.reduce((a, b) => a + b, 0) / valids.length;
      const diff = actual - prom;
      const diffStr = diff >= 0 ? `+${Math.round(diff)}` : `${Math.round(diff)}`;
      const dias = this.conectadosDias[n - 1] || 0;
      const totalDias = this.conectadosTotalDias[n - 1] || 30;
      let msg = `Conectados: ${Math.round(actual)} en promedio`;
      if (cap) msg += ` de ${cap} sucursales`;
      msg += ` (último mes). ${diffStr} vs promedio del período.`;
      if (dias < totalDias) msg += ` ⚠️ ${dias}/${totalDias} días con datos.`;
      return msg;
    },

    insightProduccion() {
      const n = this.series.length;
      if (!n) return 'Sin datos suficientes para análisis.';
      const cumplidos = this.spotsCumplen.filter(v => v === true).length;
      const total = this.spotsCumplen.filter(v => v !== null).length;
      const ultimoCumple = this.spotsCumplen[n - 1];
      const ultimoLog = this.spotsLogrados[n - 1];
      const ultimoMeta = this.spotsMeta[n - 1];
      const pct = total > 0 ? Math.round(cumplidos / total * 100) : 0;

      // Detectar rachas
      let racha = 0;
      for (let i = n - 1; i >= 0; i--) {
        if (this.spotsCumplen[i]) racha++;
        else break;
      }
      const rachaMsg = racha >= 2 ? ` (${racha} meses consecutivos ✅)` : '';

      const estado = ultimoCumple ? '✅ Meta alcanzada' : '❌ Meta no alcanzada';
      return `📊 Meta cumplida en ${cumplidos} de ${total} meses (${pct}%). ` +
        `Último mes: ${estado} (${ultimoLog} de ${ultimoMeta} spots)${rachaMsg}.`;
    },

    /* ── Chart.js Datasets ── */

    // Chart 1: Ocupación de Red (con línea de 100%)
    chartOcupacion() {
      return {
        labels: this.labels,
        datasets: [
          {
            label: 'Conectados',
            data: this.ocupacionSerie,
            fill: true,
            backgroundColor: 'rgba(16, 185, 129, 0.12)',
            borderColor: '#10b981',
            borderWidth: 2.5,
            pointRadius: 4,
            pointBackgroundColor: this.conectadosPointColors,
            pointBorderColor: this.conectadosPointColors,
            pointHoverRadius: 7,
            tension: 0.35,
          },
          {
            label: 'Capacidad total',
            data: this.conectadosCapacidad,
            fill: false,
            borderColor: 'rgba(239, 68, 68, 0.5)',
            borderWidth: 1.5,
            borderDash: [6, 4],
            pointRadius: 0,
            pointHoverRadius: 0,
          },
        ],
      };
    },

    // Chart 2: Producción Spots vs Meta
    chartProduccion() {
      const capacidad = this.series.map(s => s.capacidad_instalada ?? null);
      return {
        labels: this.labels,
        datasets: [
          { type: 'line', label: 'Meta', data: this.spotsMeta, borderColor: '#f59e0b', borderWidth: 2.5, borderDash: [6, 3], pointRadius: 5, pointBackgroundColor: '#f59e0b', pointBorderColor: '#fff', pointBorderWidth: 2, fill: false, tension: 0.15, order: 0 },
          { type: 'line', label: 'Capacidad instalada', data: capacidad, borderColor: '#ef4444', borderWidth: 1.5, borderDash: [4, 6], pointRadius: 3, pointBackgroundColor: '#ef4444', fill: false, order: 0 },
          { type: 'bar', label: 'Spots humanos', data: this.spotsHumanos, backgroundColor: 'rgba(16,185,129,0.85)', borderRadius: { topLeft: 4, topRight: 4 }, stack: 'prod', order: 1 },
          { type: 'bar', label: 'Spots IA', data: this.spotsIaLogrados, backgroundColor: 'rgba(139,92,246,0.85)', borderRadius: { topLeft: 4, topRight: 4 }, stack: 'prod', order: 1 },
        ],
      };
    },

    /* ── Chart Options ── */

    _baseChartOpts() {
      const isDark = this.isDarkNow;
      return {
        responsive: true,
        maintainAspectRatio: false,
        font: { family: "'Poppins', 'Roboto', sans-serif" },
        plugins: {
          tooltip: {
            backgroundColor: isDark ? '#27272a' : '#1e293b',
            titleFont: { family: "'Poppins', 'Roboto', sans-serif", size: 12 },
            bodyFont: { family: "'Poppins', 'Roboto', sans-serif", size: 11 },
            padding: 10,
            cornerRadius: 8,
            titleColor: '#94a3b8',
            bodyColor: '#f1f5f9',
            boxPadding: 4,
          },
          legend: {
            display: false,
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: {
              font: { family: "'Poppins', 'Roboto', sans-serif", size: 10 },
              color: isDark ? '#a1a1aa' : '#64748b',
              maxRotation: 45,
            },
          },
          y: {
            grid: {
              color: isDark ? 'rgba(113, 113, 122, 0.15)' : 'rgba(148, 163, 184, 0.2)',
              drawBorder: false,
            },
            ticks: {
              font: { family: "'Poppins', 'Roboto', sans-serif", size: 10 },
              color: isDark ? '#a1a1aa' : '#64748b',
              padding: 6,
            },
          },
        },
        interaction: {
          mode: 'index',
          intersect: false,
        },
        hover: {
          mode: 'index',
          intersect: false,
        },
        animation: {
          duration: 800,
          easing: 'easeOutQuart',
        },
      };
    },

    chartOptionsOcupacion() {
      const legacyMap = this.conectadosLegacyMap;
      return {
        ...this._baseChartOpts,
        scales: {
          ...this._baseChartOpts.scales,
          y: {
            ...this._baseChartOpts.scales.y,
            beginAtZero: true,
            ticks: {
              ...this._baseChartOpts.scales.y.ticks,
            },
          },
        },
        plugins: {
          ...this._baseChartOpts.plugins,
          legend: {
            display: false,
          },
          tooltip: {
            ...this._baseChartOpts.plugins.tooltip,
            callbacks: {
              label: (ctx) => {
                const v = ctx.raw; if (v == null) return null;
                const isConectados = ctx.datasetIndex === 0;
                if (isConectados) {
                  const i = ctx.dataIndex;
                  const tag = legacyMap[i] ? ' [legacy]' : ' [nuevo]';
                  return `  Conectados: ${Number(v).toLocaleString()}${tag}`;
                }
                return `  ${ctx.dataset.label}: ${Number(v).toLocaleString()}`;
              },
            },
          },
        },
      };
    },

    chartOptionsProduccion() {
      const d = this.isDarkNow;
      return {
        ...this._baseChartOpts,
        scales: {
          ...this._baseChartOpts.scales,
          y: { ...this._baseChartOpts.scales.y, beginAtZero: true },
        },
        plugins: {
          ...this._baseChartOpts.plugins,
          legend: { display: true, labels: { color: d ? '#d4d4d8' : '#334155', font: { size: 11 }, boxWidth: 14, usePointStyle: true, padding: 14 } },
          tooltip: {
            ...this._baseChartOpts.plugins.tooltip,
            callbacks: {
              label: (ctx) => {
                const v = ctx.raw; if (v == null) return null;
                return `  ${ctx.dataset.label}: ${Number(v).toLocaleString()}`;
              },
              footer: (items) => {
                if (!items.length) return '';
                const i = items[0].dataIndex;
                const ds = items[0].chart.data.datasets;
                let total = 0;
                for (const d of ds) {
                  if (d.type === 'bar') total += Number(d.data[i]) || 0;
                }
                return `  Total producido: ${total.toLocaleString()}`;
              },
            },
          },
        },
      };
    },

    /* ── 7b: Spots Humanos ── */
    chartSpotsHumanos() {
      const loc = this.spotsHumanos;
      const meta = this.series.map(s => { const m = (s.global?.meta_spots || 0) - (s.global?.meta_ia || 0); return m > 0 ? m : null; });
      return { labels: this.labels, datasets: [
        { type: 'line', label: 'Meta', data: meta, borderColor: '#f59e0b', borderWidth: 2.5, borderDash: [6, 3], pointRadius: 5, pointBackgroundColor: '#f59e0b', pointBorderColor: '#fff', pointBorderWidth: 2, fill: false, tension: 0.15, order: 0 },
        { type: 'bar', label: 'Logrados', data: loc, backgroundColor: 'rgba(16,185,129,0.85)', borderRadius: { topLeft: 4, topRight: 4 }, order: 1 },
      ]};
    },
    insightSpotsHumanos() {
      const u = this.ultimo; if (!u) return '';
      const l = Math.max(0, (u.global?.spots_logrados || 0) - (u.global?.spots_ia_logrados || 0));
      const m = (u.global?.meta_spots || 0) - (u.global?.meta_ia || 0);
      return m > 0 ? `${l.toLocaleString()} spots vs ${m.toLocaleString()} meta · ${Math.round(l/m*100)}%` : 'Sin meta de locución';
    },

    /* ── 7b: Spots IA ── */
    chartSpotsIa() {
      const ia = this.spotsIaLogrados;
      const meta = this.series.map(s => { const m = s.global?.meta_ia || 0; return m > 0 ? m : null; });
      return { labels: this.labels, datasets: [
        { type: 'line', label: 'Meta', data: meta, borderColor: '#f59e0b', borderWidth: 2.5, borderDash: [6, 3], pointRadius: 5, pointBackgroundColor: '#f59e0b', pointBorderColor: '#fff', pointBorderWidth: 2, fill: false, tension: 0.15, order: 0 },
        { type: 'bar', label: 'Logrados', data: ia, backgroundColor: 'rgba(139,92,246,0.85)', borderRadius: { topLeft: 4, topRight: 4 }, order: 1 },
      ]};
    },
    insightSpotsIa() {
      const u = this.ultimo; if (!u) return '';
      const ia = u.global?.spots_ia_logrados || 0;
      const m = u.global?.meta_ia || 0;
      return m > 0 ? `${ia.toLocaleString()} spots IA vs ${m.toLocaleString()} meta · ${Math.round(ia/m*100)}%` : `${ia.toLocaleString()} spots IA · sin meta`;
    },

    /* ── 7c: Comparativa Anual ── */
    chartComparativa() {
      const c = this.comparativaActual?.monthly || {};
      const p = this.comparativaAnterior?.monthly || {};
      const meses = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
      const mesActualIdx = new Date().getMonth();
      const mesActualNombre = meses[mesActualIdx];
      const keys = Object.keys(c).filter(k => k !== mesActualNombre);
      return { labels: keys, datasets: [
        // Orden del array = orden visual izquierda→derecha en Chart.js (group bar).
        // Primero: Año anterior (gris con borde), luego: Año actual (cyan).
        { type: 'bar', label: String(this.comparativaAnterior?.year || 'Anterior'), data: keys.map(k => p[k] || 0), backgroundColor: '#94a3b8', borderColor: '#475569', borderWidth: 1.5, borderRadius: 4, order: 1 },
        { type: 'bar', label: String(this.comparativaActual?.year || 'Actual'), data: keys.map(k => c[k] || 0), backgroundColor: 'rgba(8, 145, 178, 0.9)', borderColor: '#0e7490', borderWidth: 1, borderRadius: 4, order: 2 },
      ]};
    },
    /* ── Default chart options (with legend + tooltip) ── */
    chartOptionsDefault() {
      const d = this.isDarkNow;
      return {
        ...this._baseChartOpts,
        scales: {
          ...this._baseChartOpts.scales,
          y: { ...this._baseChartOpts.scales.y, beginAtZero: true },
        },
        plugins: {
          ...this._baseChartOpts.plugins,
          legend: { display: true, labels: { color: d ? '#d4d4d8' : '#334155', font: { size: 11 }, boxWidth: 14, usePointStyle: true, padding: 14 } },
          tooltip: {
            ...this._baseChartOpts.plugins.tooltip,
            backgroundColor: d ? '#1f2937' : '#fff',
            titleColor: d ? '#f9fafb' : '#111827',
            bodyColor: d ? '#d1d5db' : '#374151',
            borderColor: d ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
            borderWidth: 1,
            callbacks: {
              label: (ctx) => {
                const v = ctx.raw; if (v == null) return null;
                return `  ${ctx.dataset.label}: ${Number(v).toLocaleString()}`;
              },
            },
          },
        },
      };
    },

    /* ── 7e: Historial totales ── */
    historialTotales() {
      if (!Array.isArray(this.historialCanales)) return { altas: 0, bajas: 0, cambios: 0, deshabilitaciones: 0, reactivaciones: 0 };
      return {
        altas: this.historialCanales.filter(i => i?.accion === 'alta').length,
        bajas: this.historialCanales.filter(i => i?.accion === 'baja').length,
        cambios: this.historialCanales.filter(i => i?.accion === 'cambio').length,
        deshabilitaciones: this.historialCanales.filter(i => i?.accion === 'deshabilitacion').length,
        reactivaciones: this.historialCanales.filter(i => i?.accion === 'reactivacion').length
      };
    },

    /* ── 7f: Musicalizacion charts ── */
    musicalizacionSerieLoaded() {
      return this.musicalizacionSerie.length > 0 || this.musicalizacionCurrentMonth !== null;
    },

    _musicalizacionMerged() {
      const base = (this.musicalizacionSerie || []).map(r => ({ ...r }));
      const cur = this.musicalizacionCurrentMonth;
      if (cur && cur.mes) {
        const idx = base.findIndex(r => r.mes === cur.mes);
        if (idx >= 0) base[idx] = { ...base[idx], ...cur };
        else base.push(cur);
      }
      return base.sort((a, b) => (a.mes || '').localeCompare(b.mes || ''));
    },

    chartMusicalizacionMusical() {
      const merged = this._musicalizacionMerged;
      if (!merged.length) return { labels: [], datasets: [] };
      return {
        labels: merged.map(r => this._mesLabel(r.mes)),
        datasets: [
          {
            label: 'Actualizados',
            data: merged.map(r => r.canales_con_cumplimiento ?? 0),
            borderColor: '#06b6d4',
            backgroundColor: 'rgba(6,182,212,0.12)',
            fill: true,
            tension: 0.35,
            pointRadius: 3,
            pointHoverRadius: 5,
            borderWidth: 2,
            order: 1,
          },
          {
            label: 'Totales',
            data: merged.map(r => r.canales_total ?? 0),
            borderColor: '#64748b',
            backgroundColor: 'transparent',
            borderDash: [6, 3],
            fill: false,
            tension: 0.35,
            pointRadius: 2,
            pointHoverRadius: 4,
            borderWidth: 1.5,
            order: 2,
          },
        ],
      };
    },

    chartMusicalizacionVideo() {
      const merged = this._musicalizacionMerged;
      if (!merged.length) return { labels: [], datasets: [] };
      return {
        labels: merged.map(r => this._mesLabel(r.mes)),
        datasets: [
          {
            label: 'Actualizados',
            data: merged.map(r => r.canales_con_video_actualizadas ?? null),
            borderColor: '#8b5cf6',
            backgroundColor: 'rgba(139,92,246,0.12)',
            fill: true,
            tension: 0.35,
            pointRadius: 3,
            pointHoverRadius: 5,
            borderWidth: 2,
            order: 1,
            spanGaps: true,
          },
          {
            label: 'Totales',
            data: merged.map(r => r.canales_con_video ?? null),
            borderColor: '#64748b',
            backgroundColor: 'transparent',
            borderDash: [6, 3],
            fill: false,
            tension: 0.35,
            pointRadius: 2,
            pointHoverRadius: 4,
            borderWidth: 1.5,
            order: 2,
            spanGaps: true,
          },
        ],
      };
    },

    insightMusicalMusical() {
      const m = this._musicalizacionMerged;
      if (!m.length) return '—';
      const last = m[m.length - 1];
      const act = last.canales_con_cumplimiento ?? 0;
      const tot = last.canales_total ?? 0;
      const pct = tot > 0 ? Math.round((act / tot) * 100) : 0;
      return `${act}/${tot} (${pct}%)`;
    },

    insightMusicalVideo() {
      const m = this._musicalizacionMerged;
      if (!m.length) return '—';
      const last = m[m.length - 1];
      const act = last.canales_con_video_actualizadas;
      const tot = last.canales_con_video;
      if (tot === null || tot === undefined) return '—';
      const pct = tot > 0 ? Math.round(((act ?? 0) / tot) * 100) : 0;
      return `${act ?? 0}/${tot} (${pct}%)`;
    },

    chartOptionsMusicalizacion() {
      return {
        ...this._baseChartOpts,
        scales: {
          ...this._baseChartOpts.scales,
          y: {
            ...this._baseChartOpts.scales.y,
            beginAtZero: true,
            ticks: {
              ...this._baseChartOpts.scales.y.ticks,
              precision: 0,
            },
          },
        },
        plugins: {
          ...this._baseChartOpts.plugins,
          tooltip: {
            ...this._baseChartOpts.plugins.tooltip,
            callbacks: {
              label: (ctx) => {
                const val = ctx.parsed.y;
                if (val === null) return null;
                return `${ctx.dataset.label}: ${val}`;
              },
            },
          },
        },
      };
    },

    integridadSerieLoaded() {
      return this.integridadSerie.length > 0 || this.integridadCurrentMonth !== null;
    },

    _integridadMerged() {
      const base = (this.integridadSerie || []).map(r => ({ ...r }));
      const cur = this.integridadCurrentMonth;
      if (cur && cur.mes) {
        const idx = base.findIndex(r => r.mes === cur.mes);
        if (idx >= 0) base[idx] = { ...base[idx], ...cur };
        else base.push(cur);
      }
      return base.sort((a, b) => (a.mes || '').localeCompare(b.mes || ''));
    },

    chartIntegridad() {
      const merged = this._integridadMerged;
      if (!merged.length) return { labels: [], datasets: [] };
      return {
        labels: merged.map(r => this._mesLabel(r.mes)),
        datasets: [
          {
            label: 'Repetidas',
            data: merged.map(r => r.repetidas ?? null),
            borderColor: '#f59e0b',
            backgroundColor: 'rgba(245,158,11,0.12)',
            fill: true,
            tension: 0.35,
            pointRadius: 3,
            pointHoverRadius: 5,
            borderWidth: 2,
            spanGaps: true,
            order: 1,
          },
          {
            label: 'Sin tag',
            data: merged.map(r => r.notags ?? null),
            borderColor: '#ef4444',
            backgroundColor: 'rgba(239,68,68,0.08)',
            fill: true,
            tension: 0.35,
            pointRadius: 3,
            pointHoverRadius: 5,
            borderWidth: 2,
            spanGaps: true,
            order: 2,
          },
        ],
      };
    },

    insightIntegridad() {
      const merged = this._integridadMerged;
      if (!merged.length) return '—';
      const last = merged[merged.length - 1];
      const rep = last.repetidas ?? 0;
      const notg = last.notags ?? 0;
      return `${rep} repetidas · ${notg} sin tag`;
    },

    chartOptionsIntegridad() {
      return {
        ...this._baseChartOpts,
        scales: {
          ...this._baseChartOpts.scales,
          y: {
            ...this._baseChartOpts.scales.y,
            beginAtZero: true,
            ticks: {
              ...this._baseChartOpts.scales.y.ticks,
              precision: 0,
            },
          },
        },
        plugins: {
          ...this._baseChartOpts.plugins,
          tooltip: {
            ...this._baseChartOpts.plugins.tooltip,
            callbacks: {
              label: (ctx) => {
                const val = ctx.parsed.y ?? 0;
                return `${ctx.dataset.label}: ${val}`;
              },
            },
          },
        },
      };
    },

  },

  methods: {
    toggleSeccion(nombre) {
      this.collapsed[nombre] = !this.collapsed[nombre];
    },

    scrollToSection(id) {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },

    _formatMes(d) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      return `${y}-${m}`;
    },

    _mesLabel(yyyymm) {
      if (!yyyymm || typeof yyyymm !== 'string') return yyyymm || '';
      const [y, m] = yyyymm.split('-');
      const meses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
      const idx = parseInt(m, 10) - 1;
      return `${meses[idx] || m} ${y}`;
    },

    async cargarComparativa() {
      try {
        const res = await axiosInstance.post('/api/kpi_ops_comparativa_anual', {
          token: localStorage.getItem('token'),
          year: new Date().getFullYear(),
        });
        if (res.data?.success) {
          this.comparativaActual = res.data.current;
          this.comparativaAnterior = res.data.previous;
          this.comparativaDifPct = res.data.diferencia_pct;
        }
      } catch { this.comparativaError = 'No se pudo cargar la comparativa anual.'; }
    },

    async cargarHistorial() {
      try {
        const ultimo = this.series.length ? this.series[this.series.length - 1].mes : null;
        if (!ultimo) return;
        const res = await axiosInstance.post('/api/kpi_ops_historial_canales', {
          token: localStorage.getItem('token'),
          month: ultimo,
        });
        if (res.data?.success) {
          this.historialCanales = res.data.canales || [];
        }
      } catch { this.historialError = 'No se pudo cargar el historial de canales.'; }
    },

    async cargarMusicalizacionSerie() {
      this.musicalizacionSerieLoading = true;
      this.musicalizacionSerieError = null;
      this.musicalizacionSerie = [];
      this.musicalizacionCurrentMonth = null;
      try {
        const months = this._generarMeses(this.fechaDesde, this.fechaHasta);
        if (!months.length) return;

        const res = await axiosInstance.post('/api/kpi_ops_musicalizacion_serie', {
          token: localStorage.getItem('token'),
          months,
        });

        if (res.data?.success || res.data?.ok) {
          this.musicalizacionSerie = res.data.series || [];
          const missing = res.data.missing || [];
          if (missing.length) {
            const curRes = await axiosInstance.post('/api/kpi_ops_musicalizacion', {
              token: localStorage.getItem('token'),
              month: missing[0],
            });
            if (curRes.data?.success || curRes.data?.ok) {
              const g = curRes.data.global || {};
              this.musicalizacionCurrentMonth = {
                mes: missing[0],
                canales_total: g.canales_total ?? 0,
                canales_con_cumplimiento: g.canales_con_cumplimiento ?? 0,
                pct_canales: g.pct_canales ?? null,
                canales_con_video: g.canales_con_video ?? 0,
                canales_con_video_actualizadas: g.canales_con_video_actualizadas ?? 0,
              };
            }
          }
        } else {
          this.musicalizacionSerieError = 'No se pudo cargar la serie de musicalización.';
        }
      } catch (e) {
        if (e?.name === 'AbortError') return;
        this.musicalizacionSerieError = 'No se pudo cargar la serie de musicalización.';
      }
      this.musicalizacionSerieLoading = false;
    },


    async cargarIntegridadSerie() {
      this.integridadSerieLoading = true;
      this.integridadSerieError = null;
      this.integridadSerie = [];
      this.integridadCurrentMonth = null;
      try {
        const months = this._generarMeses(this.fechaDesde, this.fechaHasta);
        if (!months.length) return;

        const res = await axiosInstance.post('/api/kpi_ops_integridad_serie', {
          token: localStorage.getItem('token'),
          months,
        });

        if (res.data?.success || res.data?.ok) {
          this.integridadSerie = res.data.series || [];
          const missing = res.data.missing || [];
          if (missing.length) {
            const curRes = await axiosInstance.post('/api/kpi_ops_duplicados_errores', {
              token: localStorage.getItem('token'),
            });
            if (curRes.data?.success || curRes.data?.ok) {
              const d = curRes.data;
              this.integridadCurrentMonth = {
                mes: missing[0],
                repetidas: d.repetidas ?? 0,
                notags: d.notags ?? 0,
              };
            }
          }
        } else {
          this.integridadSerieError = 'No se pudo cargar la serie de integridad.';
        }
      } catch (e) {
        if (e?.name === 'AbortError') return;
        this.integridadSerieError = 'No se pudo cargar la serie de integridad.';
      }
      this.integridadSerieLoading = false;
    },


    _generarMeses(desde, hasta) {
      const d = new Date(desde.getFullYear(), desde.getMonth(), 1);
      const h = new Date(hasta.getFullYear(), hasta.getMonth(), 1);
      const meses = [];
      while (d <= h) {
        meses.push(this._formatMes(d));
        d.setMonth(d.getMonth() + 1);
      }
      return meses;
    },

    presetRange(anios) {
      this.rangoPreset = anios;
      // Usar último mes completo (no el actual) como base
      const now = new Date();
      const ultimoCompleto = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const meses = anios === '12m' ? 11 : anios === '24m' ? 23 : 35;
      const desde = new Date(ultimoCompleto.getFullYear(), ultimoCompleto.getMonth() - meses, 1);
      this.fechaDesde = desde;
      this.fechaHasta = ultimoCompleto;
      this.cargarDatos();
    },

    onRangeChange() {
      this.rangoPreset = null;
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.cargarDatos(), 300);
    },

    async cargarDatos() {
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      const signal = this.abortController.signal;
      const isReload = this.series.length > 0;
      this.isRefreshing = isReload;
      this.sectionLoading = !isReload;
      this.loading = true;
      this.error = null;
      this.series = [];
      this.historialCanales = [];
      this.musicalizacionSerie = [];
      this.musicalizacionCurrentMonth = null;
      this.integridadSerie = [];
      this.integridadCurrentMonth = null;
      this.comparativaActual = null;
      this.comparativaAnterior = null;
      this.comparativaDifPct = null;

      const months = this._generarMeses(this.fechaDesde, this.fechaHasta);
      if (!months.length) {
        this.loading = false;
        return;
      }

      try {
        const res = await axiosInstance.post('/api/kpi_ops_analitica', {
          token: localStorage.getItem('token'),
          months,
        }, { signal });

        if (res.data.success || res.data.ok) {
          this.series = res.data.series || [];
          this.cargarComparativa();
          this.cargarHistorial();
          this.cargarMusicalizacionSerie();
          this.cargarIntegridadSerie();
        } else {
          this.error = res.data.message || res.data.msg || 'Error al cargar analítica.';
        }        } catch (e) {
        if (e?.name === 'AbortError') return;
        console.warn('Error loading analytics data');
        this.error = 'Error de conexión al cargar analítica.';
      } finally {
        this.loading = false;
        this.sectionLoading = false;
        this.isRefreshing = false;
      }
    },

    // ── Historico por marca (radio o cliente) ──
    async cargarRadiosOptions() {
      try {
        const ahora = new Date();
        const mesActual = `${ahora.getFullYear()}-${String(ahora.getMonth() + 1).padStart(2, '0')}`;
        const r2 = await axiosInstance.post('/api/kpi_ops_mes', {
          token: localStorage.getItem('token'),
          month: mesActual,
        });
        const canales = (r2.data?.data?.canales) || (r2.data?.canales) || [];
        // Radios (tienen radio_id) y clientes sin radio (tienen client_id con radio_id null/0)
        this.radiosOptions = canales
          .filter(c => c.radio_id || c.client_id)
          .map(c => {
            if (c.radio_id && (c.radio_type !== 'Sin canal')) {
              return {
                label: c.radio_name,
                value: `r:${c.radio_id}`,
                kind: 'radio',
                stream: c.stream,
                meta_actual: c.meta_spots,
              };
            }
            // Cliente sin radio
            return {
              label: c.client_name || c.radio_name,
              value: `c:${c.client_id}`,
              kind: 'client',
              stream: null,
              meta_actual: c.meta_spots,
            };
          })
          .sort((a, b) => a.label.localeCompare(b.label, 'es'));
      } catch (e) {
        console.error('Error cargando radios para por-marca:', e);
        this.porMarcaError = 'No se pudo cargar la lista de canales.';
      }
    },

    async cargarPorMarcaMulti() {
      if (!this.porMarcaCanalesSeleccionados.length || this.porMarcaLoading) return;
      this.porMarcaLoading = true;
      this.porMarcaError = null;
      try {
        const months = this._generarMeses(this.fechaDesde, this.fechaHasta);
        const radioIds = [];
        const clientIds = [];
        for (const val of this.porMarcaCanalesSeleccionados) {
          const opt = this.radiosOptions.find(r => r.value === val);
          if (!opt) continue;
          const num = parseInt(val.slice(2), 10);
          if (Number.isNaN(num) || num <= 0) continue;
          if (opt.kind === 'client') clientIds.push(num);
          else radioIds.push(num);
        }
        if (!radioIds.length && !clientIds.length) {
          this.porMarcaError = 'No se pudieron identificar los canales seleccionados.';
          this.porMarcaLoading = false;
          return;
        }
        const params = { token: localStorage.getItem('token'), months };
        if (radioIds.length)  params.radio_ids  = radioIds;
        if (clientIds.length) params.client_ids = clientIds;
        const res = await axiosInstance.post('/api/kpi_ops_spots_por_marca', params);
        if (res.data?.success) {
          this.porMarcaMultiData = res.data.items || [];
          this.porMarcaCanalesConsultados = [...this.porMarcaCanalesSeleccionados].sort();
          this.porMarcaMesesConsultados = [...months].sort();
          this.porMarcaTimestamp = new Date();
        } else {
          this.porMarcaError = res.data?.message || res.data?.error || 'No se pudo cargar el historico.';
        }
      } catch (e) {
        this.porMarcaError = 'Error de conexion al cargar el historico por marca.';
      } finally {
        this.porMarcaLoading = false;
      }
    },

    _canalesKey(list) {
      return [...(list || [])].sort().join(',');
    },

    formatMesCorto(mes) {
      if (!mes || typeof mes !== 'string') return mes;
      const meses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
      const [y, m] = mes.split('-');
      const idx = parseInt(m, 10) - 1;
      if (idx < 0 || idx > 11) return mes;
      return `${meses[idx]} ${y.slice(2)}`;
    },

    _exportTimestamp() {
      const d = new Date();
      const pad = n => String(n).padStart(2, '0');
      return `${d.getFullYear()}${pad(d.getMonth()+1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}`;
    },

    _buildExportRows() {
      const months = this.porMarcaMesesConsultados || [];
      const rows = this.porMarcaMultiData.map(canal => {
        const r = { Canal: canal.name };
        let tH = 0, tI = 0, tMH = 0, tMI = 0;
        for (const mes of months) {
          const d = canal.series.find(s => s.mes === mes);
          const h = Number(d?.spots_humanos) || 0;
          const i = Number(d?.spots_ia) || 0;
          const mh = Number(d?.meta_humano) || 0;
          const mi = Number(d?.meta_ia) || 0;
          r[`${mes} H`] = h;
          r[`${mes} IA`] = i;
          r[`${mes} Meta H`] = mh;
          r[`${mes} Meta IA`] = mi;
          tH += h; tI += i; tMH += mh; tMI += mi;
        }
        r['Total H'] = tH;
        r['Total IA'] = tI;
        r['Total Meta H'] = tMH;
        r['Total Meta IA'] = tMI;
        return r;
      });
      return { headers: Object.keys(rows[0] || {}), rows, months };
    },

    async _captureExportNode() {
      const node = this.$refs.porMarcaExportNode;
      if (!node) return null;
      const clone = node.cloneNode(true);
      clone.style.opacity = '1';
      clone.style.backgroundColor = this.isDarkNow ? '#18181b' : '#ffffff';
      document.body.appendChild(clone);
      try {
        return await toCanvas(clone, { pixelRatio: 2 });
      } finally {
        document.body.removeChild(clone);
      }
    },

    async exportarPNG() {
      this.exportLoading = true;
      try {
        const canvas = await this._captureExportNode();
        if (!canvas) return;
        const link = document.createElement('a');
        link.download = `historico-canales_${this._exportTimestamp()}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
      } catch (e) {
        console.error('Error exportando PNG:', e);
        this.$toast?.add({ severity: 'error', summary: 'Error', detail: 'No se pudo exportar la imagen.', life: 4000, group: 'topr' });
      } finally {
        this.exportLoading = false;
      }
    },

    async copiarPNG() {
      this.exportLoading = true;
      try {
        const canvas = await this._captureExportNode();
        if (!canvas) return;
        canvas.toBlob(async (blob) => {
          if (!blob) return;
          try {
            await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
            this.$toast?.add({ severity: 'success', summary: 'Copiado', detail: 'Imagen copiada al portapapeles.', life: 3000, group: 'topr' });
          } catch {
            this.$toast?.add({ severity: 'warn', summary: 'No disponible', detail: 'Tu navegador no soporta copiar imágenes al portapapeles.', life: 4000, group: 'topr' });
          } finally {
            this.exportLoading = false;
          }
        }, 'image/png');
      } catch (e) {
        console.error('Error copiando PNG:', e);
        this.exportLoading = false;
      }
    },

    exportarXLSX() {
      const { rows, months } = this._buildExportRows();
      if (!rows.length) return;
      const headerMap = { Canal: 'Canal' };
      for (const mes of months) {
        const lbl = this.formatMesCorto(mes);
        headerMap[`${mes} H`] = `${lbl} H`;
        headerMap[`${mes} IA`] = `${lbl} IA`;
        headerMap[`${mes} Meta H`] = `${lbl} Meta H`;
        headerMap[`${mes} Meta IA`] = `${lbl} Meta IA`;
      }
      headerMap['Total H'] = 'Total H';
      headerMap['Total IA'] = 'Total IA';
      headerMap['Total Meta H'] = 'Total Meta H';
      headerMap['Total Meta IA'] = 'Total Meta IA';
      const flatRows = rows.map(r => {
        const row = {};
        for (const [k, label] of Object.entries(headerMap)) {
          row[label] = r[k];
        }
        return row;
      });
      const headers = Object.values(headerMap);
      const ws = XLSX.utils.json_to_sheet(flatRows, { header: headers });
      const colWidths = headers.map(h => ({ wch: Math.max(h.length + 2, 10) }));
      ws['!cols'] = colWidths;
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Histórico');
      XLSX.writeFile(wb, `historico-canales_${this._exportTimestamp()}.xlsx`);
    },

    async exportarPDF() {
      this.exportLoading = true;
      try {
        const canvas = await this._captureExportNode();
        if (!canvas) return;
        const imgData = canvas.toDataURL('image/png');
        const imgW = canvas.width;
        const imgH = canvas.height;

        const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
        const pageW = doc.internal.pageSize.getWidth();
        const pageH = doc.internal.pageSize.getHeight();

        // Title
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(13);
        doc.setTextColor(31, 41, 55);
        doc.text('Histórico por canal', 14, 14);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(100, 116, 139);
        const ts = this.porMarcaTimestamp;
        const tsLabel = ts ? `${ts.toLocaleDateString()} ${this.porMarcaTimestampLabel}` : '';
        const { rows, months } = this._buildExportRows();
        doc.text(`${rows.length} canal${rows.length !== 1 ? 'es' : ''} · ${months.length} meses${tsLabel ? ' · Consultado ' + tsLabel : ''}`, 14, 19);

        // Image scaled to fit page
        const marginX = 14;
        const maxW = pageW - marginX * 2;
        const startY = 24;
        const maxH = pageH - startY - 10;
        const ratio = imgW / imgH;
        let fitW = maxW;
        let fitH = fitW / ratio;
        if (fitH > maxH) {
          fitH = maxH;
          fitW = fitH * ratio;
        }
        doc.addImage(imgData, 'PNG', marginX, startY, fitW, fitH);

        doc.save(`historico-canales_${this._exportTimestamp()}.pdf`);
      } catch (e) {
        console.error('Error exportando PDF:', e);
        this.$toast?.add({ severity: 'error', summary: 'Error', detail: 'No se pudo exportar el PDF.', life: 4000, group: 'topr' });
      } finally {
        this.exportLoading = false;
      }
    },
  },

  watch: {
    // Si cambia el rango mientras hay datos cargados, marcarlos como desactualizados
    // (no recargamos automaticamente; el usuario debe hacer click en "Consultar")
  },

  mounted() {
    this.isDarkNow = document.documentElement.classList.contains('dark');
    this._darkObserver = new MutationObserver(() => {
      this.isDarkNow = document.documentElement.classList.contains('dark');
    });
    this._darkObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    this.cargarDatos();
    this.cargarRadiosOptions();
  },

  beforeUnmount() {
    if (this._darkObserver) this._darkObserver.disconnect();
    if (this._darkObserver) this._darkObserver.disconnect();
  },
};
</script>

<style scoped>
@import "@/assets/media-module-base.css";

/* ── Page theme ── */
.page-ind-analitica {
  max-width: 1240px;
}

/* ── Quick Nav ── */
.analitica-quick-nav {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.85rem 1.1rem;
  justify-content: center;
  background: rgba(255, 255, 255, 0.65);
  border-radius: 16px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  position: sticky;
  top: 0.6rem;
  z-index: 20;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05), 0 1px 3px rgba(0,0,0,0.03);
}
.dark .analitica-quick-nav {
  background: rgba(24, 24, 27, 0.55);
  border-color: rgba(63, 63, 70, 0.45);
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
}
.analitica-quick-nav-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.015em;
  color: #475569;
  background: white;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
  white-space: nowrap;
  outline: none;
  user-select: none;
  line-height: 1.1;
}
.analitica-quick-nav-btn:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
  color: #0f172a;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px) scale(1.02);
}
.analitica-quick-nav-btn:active {
  transform: translateY(0) scale(0.97);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}
.dark .analitica-quick-nav-btn {
  color: #a1a1aa;
  background: rgba(39, 39, 42, 0.35);
  border-color: rgba(63, 63, 70, 0.35);
}
.dark .analitica-quick-nav-btn:hover {
  background: rgba(39, 39, 42, 0.65);
  border-color: rgba(113, 113, 122, 0.35);
  color: #f4f4f5;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  transform: translateY(-2px) scale(1.02);
}
.analitica-quick-nav-btn i { transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1); }
.analitica-quick-nav-btn:hover i { transform: scale(1.15); }

/* Per-section color accents */
.analitica-quick-nav-btn[data-section="spots"]           { background: rgba(59,130,246,0.08); border-color: rgba(59,130,246,0.15); }
.analitica-quick-nav-btn[data-section="spots"]:hover      { background: rgba(59,130,246,0.15); border-color: rgba(59,130,246,0.25); }
.analitica-quick-nav-btn[data-section="humanos"]          { background: rgba(16,185,129,0.08); border-color: rgba(16,185,129,0.15); }
.analitica-quick-nav-btn[data-section="humanos"]:hover    { background: rgba(16,185,129,0.15); border-color: rgba(16,185,129,0.25); }
.analitica-quick-nav-btn[data-section="ia"]               { background: rgba(139,92,246,0.08); border-color: rgba(139,92,246,0.15); }
.analitica-quick-nav-btn[data-section="ia"]:hover         { background: rgba(139,92,246,0.15); border-color: rgba(139,92,246,0.25); }
.analitica-quick-nav-btn[data-section="comparativa"]      { background: rgba(99,102,241,0.08); border-color: rgba(99,102,241,0.15); }
.analitica-quick-nav-btn[data-section="comparativa"]:hover{ background: rgba(99,102,241,0.15); border-color: rgba(99,102,241,0.25); }
.analitica-quick-nav-btn[data-section="conectados"]       { background: rgba(16,185,129,0.08); border-color: rgba(16,185,129,0.15); }
.analitica-quick-nav-btn[data-section="conectados"]:hover { background: rgba(16,185,129,0.15); border-color: rgba(16,185,129,0.25); }
.analitica-quick-nav-btn[data-section="musicalizacion"]   { background: rgba(6,182,212,0.08); border-color: rgba(6,182,212,0.15); }
.analitica-quick-nav-btn[data-section="musicalizacion"]:hover { background: rgba(6,182,212,0.15); border-color: rgba(6,182,212,0.25); }
.analitica-quick-nav-btn[data-section="canales"]          { background: rgba(249,115,22,0.08); border-color: rgba(249,115,22,0.15); }
.analitica-quick-nav-btn[data-section="canales"]:hover    { background: rgba(249,115,22,0.15); border-color: rgba(249,115,22,0.25); }

/* ── Toolbar ── */
.ind-toolbar {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: space-between;
}
.ind-toolbar-left {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  flex-wrap: wrap;
}
.ind-toolbar-field { width: 140px; flex-shrink: 0; }
.ind-month-picker .p-inputtext { height: 40px; border-radius: 10px; font-size: 0.85rem; }
.ind-toolbar-actions { display: flex; gap: 5px; padding-bottom: 1px; }

.ind-range-btn {
  height: 34px;
  font-size: 0.7rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.03em;
  border-radius: 8px;
  border: 1px solid rgba(148, 163, 184, 0.45);
  background: rgba(255, 255, 255, 0.94);
  color: #475569;
  padding: 0 10px;
  transition: all 0.18s ease;
}
.ind-range-btn:hover { border-color: rgba(99, 102, 241, 0.5); background: rgba(239, 246, 255, 0.95); color: #4f46e5; }
.ind-range-btn-active { border-color: rgba(99, 102, 241, 0.65); background: rgba(219, 234, 254, 0.9); color: #1d4ed8; box-shadow: inset 0 0 0 1px rgba(147, 197, 253, 0.4); }
.dark .ind-range-btn { border-color: rgba(113, 113, 122, 0.48); background: rgba(24, 24, 27, 0.75); color: #d4d4d8; }
.dark .ind-range-btn:hover { border-color: rgba(129, 140, 248, 0.5); background: rgba(30, 41, 59, 0.85); color: #a5b4fc; }
.dark .ind-range-btn-active { border-color: rgba(96, 165, 250, 0.75); background: rgba(30, 58, 138, 0.3); color: #dbeafe; box-shadow: inset 0 0 0 1px rgba(59, 130, 246, 0.35); }

/* ── Aviso mes en curso ── */
.analitica-aviso-box {
  display: flex; align-items: center; padding: 8px 14px; border-radius: 8px;
  border: 1px solid rgba(251, 191, 36, 0.4); background: rgba(254, 243, 199, 0.5);
  color: #92400e; font-size: 0.78rem; font-weight: 500;
}
.dark .analitica-aviso-box { background: rgba(69, 48, 10, 0.4); color: #fde68a; border-color: rgba(251, 191, 36, 0.3); }

/* ── Error ── */
.kpi-error-box {
  display: flex; align-items: center; padding: 10px 14px; border-radius: 8px;
  border: 1px solid rgba(239, 68, 68, 0.4); background: rgba(254, 226, 226, 0.5);
  color: #b91c1c; font-size: 0.9rem;
}
.dark .kpi-error-box { background: rgba(69, 10, 10, 0.4); color: #fca5a5; border-color: rgba(239, 68, 68, 0.3); }

/* ── Page content ── */
.analitica-page-content { animation: kpi-content-fade-in 0.35s ease-out; }
.analitica-sections-refreshing { opacity: 0.62; transition: opacity 0.25s ease; pointer-events: none; }

/* ── Chart render ── */
.analitica-chart-render { width: 100%; height: 280px; }

/* ── Insights ── */
.analitica-insight {
  display: flex; align-items: flex-start; gap: 6px; margin-top: 10px;
  padding: 8px 12px; border-radius: 8px;
  background: rgba(148, 163, 184, 0.06); border: 1px solid rgba(148, 163, 184, 0.15);
  font-size: 0.78rem; font-weight: 500; color: #64748b; line-height: 1.5;
}
.analitica-insight i { margin-top: 2px; flex-shrink: 0; color: #94a3b8; }
.dark .analitica-insight { background: rgba(113, 113, 122, 0.08); border-color: rgba(113, 113, 122, 0.15); color: #a1a1aa; }
.dark .analitica-insight i { color: #71717a; }

/* ── Section states ── */
.analitica-section-error,
.analitica-section-loading,
.analitica-section-empty { padding: 12px 14px; border-radius: 8px; font-size: 0.8rem; font-weight: 500; text-align: center; }
.analitica-section-error { background: rgba(239, 68, 68, 0.08); border: 1px solid rgba(239, 68, 68, 0.2); color: #dc2626; }
.dark .analitica-section-error { background: rgba(239, 68, 68, 0.1); color: #fca5a5; }
.analitica-section-loading { color: #94a3b8; }
.analitica-section-empty { color: #94a3b8; }

/* ── Empty state ── */
.analitica-empty {
  border: 1px dashed rgba(148, 163, 184, 0.9); border-radius: 12px; min-height: 200px;
  display: flex; flex-direction: column; justify-content: center; align-items: center;
  gap: 6px; color: #64748b; padding: 16px;
}
.dark .analitica-empty { border-color: rgba(82, 82, 91, 0.8); color: #a1a1aa; }
.analitica-empty-icon { font-size: 2rem; opacity: 0.5; }
.analitica-empty-title { margin: 0; font-weight: 600; font-size: 0.95rem; }
.analitica-empty-hint { margin: 0; font-size: 0.82rem; }

/* ── Skeletons ── */
.analitica-skel-page { animation: kpi-skel-fade-in 0.4s ease-out; }

/* ── Comparativa diff badge ── */
.analitica-comparativa-diff { font-size: 0.85rem; font-weight: 600; margin-left: 6px; }
.analitica-diff--pos { color: #10b981; }
.analitica-diff--neg { color: #ef4444; }

/* ── Historial badges ── */
.analitica-historial-badges { display: flex; gap: 6px; margin-left: 8px; }
.analitica-badge { padding: 2px 8px; border-radius: 4px; font-size: 0.7rem; font-weight: 600; }
.analitica-badge.alta { background: rgba(16,185,129,0.15); color: #10b981; }
.analitica-badge.baja { background: rgba(239,68,68,0.15); color: #ef4444; }
.analitica-badge.deshabilitacion { background: rgba(249,115,22,0.15); color: #f97316; }
.analitica-badge.reactivacion { background: rgba(59,130,246,0.15); color: #3b82f6; }
.analitica-badge.cambio { background: rgba(245,158,11,0.15); color: #f59e0b; }
.analitica-badge.legacy { background: rgba(245,158,11,0.15); color: #f59e0b; font-size: 0.6rem; letter-spacing: 0.05em; text-transform: uppercase; }

/* ── Histórico por Canal (tabla multi-canal) ── */
.hist-canal-controls {
  display: flex; gap: 12px; align-items: center; flex-wrap: wrap;
  background: rgba(255,255,255,0.7);
  border: 1px solid rgba(148,163,184,0.3);
  border-radius: 10px;
  padding: 12px 14px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.03);
}
.dark .hist-canal-controls {
  background: rgba(24,24,27,0.6);
  border-color: rgba(82,82,91,0.5);
  box-shadow: 0 1px 2px rgba(0,0,0,0.2);
}
.hist-canal-selector {
  display: flex; align-items: center; gap: 10px;
  flex: 1; min-width: 340px; position: relative;
}
.hist-canal-selector > i.pi-search {
  color: #94a3b8; font-size: 0.9rem;
  position: absolute; left: 12px; top: 50%; transform: translateY(-50%);
  z-index: 2; pointer-events: none;
}
.hist-canal-multiselect { width: 100%; }
.hist-canal-multiselect :deep(.p-multiselect) {
  padding-left: 34px; border-radius: 8px; min-height: 42px;
  background: #fff; border-color: rgba(148,163,184,0.4);
}
.hist-canal-multiselect :deep(.p-multiselect:not(.p-disabled):hover) {
  border-color: rgba(99,102,241,0.5);
}
.hist-canal-multiselect :deep(.p-multiselect-label) {
  padding: 8px 8px 8px 0; font-size: 0.85rem; color: #1f2937;
}
.hist-canal-multiselect :deep(.p-multiselect-label.p-placeholder) {
  color: #94a3b8;
}
.hist-canal-multiselect :deep(.p-multiselect-token) {
  background: rgba(99,102,241,0.12); color: #4338ca; border-radius: 14px;
  font-size: 0.78rem; padding: 2px 8px; margin-right: 4px;
}
.dark .hist-canal-multiselect :deep(.p-multiselect) {
  background: rgba(39,39,42,0.7); border-color: rgba(82,82,91,0.5);
}
.dark .hist-canal-multiselect :deep(.p-multiselect-label) { color: #e4e4e7; }
.dark .hist-canal-multiselect :deep(.p-multiselect-label.p-placeholder) { color: #71717a; }
.dark .hist-canal-multiselect :deep(.p-multiselect-token) {
  background: rgba(99,102,241,0.25); color: #c7d2fe;
}

.hist-canal-actions { display: flex; gap: 12px; align-items: center; flex-wrap: wrap; }
.hist-canal-btn-pending { position: relative; box-shadow: 0 0 0 2px rgba(245,158,11,0.4); }
.hist-canal-btn-pending::after {
  content: ''; position: absolute; top: -3px; right: -3px; width: 10px; height: 10px;
  background: #f59e0b; border-radius: 50%; border: 2px solid #fff;
  animation: hist-canal-pulse 1.8s ease-in-out infinite;
}
.dark .hist-canal-btn-pending::after { border-color: #18181b; }
@keyframes hist-canal-pulse {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.25); opacity: 0.6; }
}
.hist-canal-pending-badge {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 0.75rem; color: #b45309; font-weight: 600;
  background: rgba(245,158,11,0.12);
  padding: 4px 10px; border-radius: 12px;
  border: 1px solid rgba(245,158,11,0.25);
}
.dark .hist-canal-pending-badge {
  color: #fcd34d; background: rgba(245,158,11,0.15);
  border-color: rgba(245,158,11,0.3);
}
.hist-canal-last-ok {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 0.75rem; color: #047857; font-weight: 500;
  background: rgba(16,185,129,0.1);
  padding: 4px 10px; border-radius: 12px;
  border: 1px solid rgba(16,185,129,0.25);
}
.dark .hist-canal-last-ok {
  color: #6ee7b7; background: rgba(16,185,129,0.15);
  border-color: rgba(16,185,129,0.3);
}

.hist-canal-empty { text-align: center; padding: 50px 20px; color: #94a3b8; }
.hist-canal-empty-icon { font-size: 2.2rem; opacity: 0.35; display: block; margin-bottom: 10px; }
.hist-canal-empty-title { font-weight: 600; font-size: 0.95rem; margin: 0 0 4px; color: #64748b; }
.hist-canal-empty-hint { font-size: 0.82rem; margin: 0; color: #94a3b8; }
.dark .hist-canal-empty-title { color: #cbd5e1; }
.dark .hist-canal-empty-hint { color: #71717a; }

.hist-canal-error { background: rgba(239,68,68,0.08); border: 1px solid rgba(239,68,68,0.2); color: #dc2626; padding: 10px 14px; border-radius: 8px; font-size: 0.85rem; margin-top: 8px; }
.dark .hist-canal-error { background: rgba(239,68,68,0.15); color: #fca5a5; border-color: rgba(239,68,68,0.3); }

/* Tabla */
.hist-canal-table { font-size: 0.8rem; }
.hist-canal-table :deep(.p-datatable-wrapper) { border-radius: 10px; }
.hist-canal-table :deep(.p-datatable-thead > tr > th) {
  text-align: center; font-weight: 700; font-size: 0.72rem;
  text-transform: uppercase; letter-spacing: 0.06em;
  background: #f8fafc;        /* light: opaco (slate-50) */
  color: #1e293b;             /* light: slate-800 — contraste ~12:1 (AAA) */
  border: none;
  border-bottom: 2px solid #cbd5e1;
  padding: 12px 10px;
  white-space: nowrap;
}
.hist-canal-table :deep(.p-datatable-tbody > tr > td) {
  vertical-align: middle; padding: 6px 6px;
  border-color: rgba(148, 163, 184, 0.25);
  background: #ffffff;        /* light: filas opacas */
}
.hist-canal-table :deep(.p-datatable-tbody > tr:hover > td) {
  background: rgba(99, 102, 241, 0.05) !important;
}
.hist-canal-table :deep(.p-datatable-tbody > tr:nth-child(even) > td) {
  background: #f1f5f9;        /* light: zebra opaco (slate-100) */
}
.hist-canal-name {
  display: flex; gap: 8px; align-items: center;
  font-weight: 600; font-size: 0.85rem; padding: 0 8px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%;
  color: #1f2937;
}
.hist-canal-name i { font-size: 0.85rem; flex-shrink: 0; }
.hist-cell-no-data { color: #94a3b8; text-align: center; opacity: 0.5; font-size: 0.8rem; }

/* ── Frozen columns: fondo opaco + sombra para separar visualmente ── */
.hist-canal-table :deep(.p-frozen-column) {
  background: #ffffff !important;
  box-shadow: 2px 0 4px -2px rgba(15, 23, 42, 0.12);
  z-index: 2;
}
.hist-canal-table :deep(.p-datatable-tbody > tr:nth-child(even) > td.p-frozen-column) {
  background: #f1f5f9 !important;        /* light zebra opaco */
}
.hist-canal-table :deep(.p-datatable-thead > tr > th.p-frozen-column) {
  background: #f8fafc !important;        /* light header opaco */
}

/* ── Chart subtitle ── */
.analitica-chart-subtitle { font-size: 0.65rem; font-weight: 500; color: #94a3b8; margin-left: 2px; }

/* ── DataTable dark mode ── */
:deep(.p-datatable) .p-datatable-thead > tr > th {
  background: rgba(255,255,255,0.6); color: #64748b; border-color: rgba(148,163,184,0.3); font-size: 0.78rem;
}
:deep(.p-datatable) .p-datatable-tbody > tr > td { border-color: rgba(148,163,184,0.3); font-size: 0.8rem; }
.dark :deep(.p-datatable) .p-datatable-thead > tr > th { background: rgba(24,24,27,0.5); color: #a1a1aa; }
.dark :deep(.p-datatable) .p-datatable-tbody > tr { color: #d4d4d8; }
.dark :deep(.p-datatable) .p-datatable-tbody > tr:nth-child(even) { background: rgba(39, 39, 42, 0.3); }
.dark :deep(.p-datatable) .p-paginator { background: rgba(24,24,27,0.5); color: #a1a1aa; border-color: rgba(82,82,91,0.4); }

/* ── Responsive ── */
@media (max-width: 768px) {
  .ind-toolbar { flex-direction: column; align-items: stretch; }
  .ind-toolbar-left { flex-direction: column; align-items: stretch; }
  .ind-toolbar-field { width: 100%; }
  .ind-toolbar-actions { flex-wrap: wrap; }
  .analitica-chart-render { height: 200px; }
  .ind-range-btn { min-height: 44px; font-size: 0.75rem !important; padding: 0 12px; }
}
@media (max-width: 640px) {
  .analitica-quick-nav { gap: 0.35rem; padding: 0.6rem 0.8rem; }
  .analitica-quick-nav-btn { padding: 0.4rem 0.7rem; font-size: 0.7rem; }
}
@media (prefers-reduced-motion: reduce) {
  .kpi-section, .kpi-mus-hero, .kpi-mus-strip .kpi-mus-stat { animation: none !important; }
  .kpi-collapse-enter-active, .kpi-collapse-leave-active { transition: none !important; }
}
</style>

<!-- ── Estilos globales (sin scoped) para dark mode ── -->
<!-- Estos selectores apuntan a `.dark` que se aplica en <html> por src/theme.js -->
<style>
/* ── Histórico por Canal · Light mode (base) ── */
.hist-canal-table .p-datatable-thead > tr > th {
  background: #f8fafc !important;
  color: #1e293b !important;
  border: none !important;
  border-bottom: 2px solid #cbd5e1 !important;
  text-align: center;
  font-weight: 700;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 12px 10px;
  white-space: nowrap;
}
.hist-canal-table .p-datatable-tbody > tr > td {
  border-color: rgba(148, 163, 184, 0.25) !important;
  background: #ffffff !important;
  padding: 6px;
  vertical-align: middle;
}
.hist-canal-table .p-datatable-tbody > tr:hover > td {
  background: rgba(99, 102, 241, 0.05) !important;
}
.hist-canal-table .p-datatable-tbody > tr:nth-child(even) > td {
  background: #f1f5f9 !important;
}
.hist-canal-name {
  display: flex; gap: 8px; align-items: center;
  font-weight: 600; font-size: 0.85rem; padding: 0 8px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  max-width: 100%;
  color: #1f2937;
}

/* Frozen columns: position sticky + fondo opaco + sombra */
.hist-canal-table .p-frozen-column {
  position: sticky !important;
  background: #ffffff !important;
  box-shadow: 2px 0 4px -2px rgba(15, 23, 42, 0.12) !important;
  z-index: 2 !important;
}
.hist-canal-table .p-frozen-column.p-datatable-frozen-column-firstcell { left: 0 !important; }
.hist-canal-table .p-frozen-column.p-datatable-frozen-column-lastcell { right: 0 !important; }
.hist-canal-table .p-datatable-tbody > tr:nth-child(even) > td.p-frozen-column {
  background: #f1f5f9 !important;
}
.hist-canal-table .p-datatable-thead > tr > th.p-frozen-column {
  background: #f8fafc !important;
}

/* ── Histórico por Canal · Dark mode global ── */
.dark .hist-canal-table .p-datatable-thead > tr > th {
  background: #27272a !important;
  color: #f1f5f9 !important;
  border-bottom: 2px solid #3f3f46 !important;
}
.dark .hist-canal-table .p-datatable-tbody > tr > td {
  border-color: rgba(82, 82, 91, 0.3) !important;
  background: #18181b !important;
}
.dark .hist-canal-table .p-datatable-tbody > tr:hover > td {
  background: rgba(99, 102, 241, 0.1) !important;
}
.dark .hist-canal-table .p-datatable-tbody > tr:nth-child(even) > td {
  background: #1f1f23 !important;
}
.dark .hist-canal-name { color: #f1f5f9 !important; }

/* Frozen columns dark: fondo opaco para que no se atraviesen con el scroll */
.dark .hist-canal-table .p-frozen-column {
  background: #18181b !important;
  box-shadow: 2px 0 4px -2px rgba(0, 0, 0, 0.5) !important;
}
.dark .hist-canal-table .p-datatable-tbody > tr:nth-child(even) > td.p-frozen-column {
  background: #1f1f23 !important;
}
.dark .hist-canal-table .p-datatable-thead > tr > th.p-frozen-column {
  background: #27272a !important;
}

/* ── Export toolbar ── */
.hist-canal-export {
  display: flex;
  gap: 2px;
  align-items: center;
}
.hist-canal-export-btn.p-button {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  transition: all 0.2s ease;
}
/* PNG — amarillo */
.hist-canal-export .export-png.p-button { color: #eab308 !important; }
.hist-canal-export .export-png.p-button:hover { background: rgba(234, 179, 8, 0.12) !important; color: #ca8a04 !important; }
/* Copy — azul */
.hist-canal-export .export-copy.p-button { color: #3b82f6 !important; }
.hist-canal-export .export-copy.p-button:hover { background: rgba(59, 130, 246, 0.12) !important; color: #2563eb !important; }
/* XLSX — verde */
.hist-canal-export .export-xlsx.p-button { color: #22c55e !important; }
.hist-canal-export .export-xlsx.p-button:hover { background: rgba(34, 197, 94, 0.12) !important; color: #16a34a !important; }
/* PDF — rojo */
.hist-canal-export .export-pdf.p-button { color: #ef4444 !important; }
.hist-canal-export .export-pdf.p-button:hover { background: rgba(239, 68, 68, 0.12) !important; color: #dc2626 !important; }
/* Dark mode */
.dark .hist-canal-export .export-png.p-button { color: #fbbf24 !important; }
.dark .hist-canal-export .export-png.p-button:hover { background: rgba(251, 191, 36, 0.15) !important; }
.dark .hist-canal-export .export-copy.p-button { color: #60a5fa !important; }
.dark .hist-canal-export .export-copy.p-button:hover { background: rgba(96, 165, 250, 0.15) !important; }
.dark .hist-canal-export .export-xlsx.p-button { color: #4ade80 !important; }
.dark .hist-canal-export .export-xlsx.p-button:hover { background: rgba(74, 222, 128, 0.15) !important; }
.dark .hist-canal-export .export-pdf.p-button { color: #f87171 !important; }
.dark .hist-canal-export .export-pdf.p-button:hover { background: rgba(248, 113, 113, 0.15) !important; }

/* ── Offscreen export table (hidden from view) ── */
.por-marca-export-wrap {
  position: fixed;
  left: 0;
  top: 0;
  opacity: 0;
  pointer-events: none;
  background: #ffffff;
  padding: 16px;
}
.por-marca-export-table {
  table-layout: fixed;
  border-collapse: collapse;
  font-family: 'Poppins', 'Roboto', sans-serif;
  border: 1px solid #cbd5e1;
}
.por-marca-export-table th {
  background: #f1f5f9;
  color: #1e293b;
  border: none;
  border-bottom: 2px solid #cbd5e1;
  border-right: 1px solid #e2e8f0;
  text-align: center;
  font-weight: 700;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 10px 8px;
  white-space: nowrap;
  overflow: hidden;
}
.por-marca-export-table th:last-child {
  border-right: none;
}
.por-marca-export-table td {
  border: none;
  border-bottom: 1px solid #e2e8f0;
  border-right: 1px solid #e2e8f0;
  background: #ffffff;
  padding: 6px;
  vertical-align: middle;
  overflow: hidden;
}
.por-marca-export-table td:last-child {
  border-right: none;
}
.por-marca-export-td-canal {
  vertical-align: middle;
  padding: 6px 12px;
  font-weight: 600;
  font-size: 0.85rem;
  color: #1f2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.por-marca-export-td-cell {
  vertical-align: middle;
  text-align: center;
}
.por-marca-export-table tr:nth-child(even) td {
  background: #f8fafc;
}
.por-marca-export-table tr:last-child td {
  border-bottom: none;
}

/* ── Export overlay ── */
.export-overlay {
  position: fixed;
  inset: 0;
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(4px);
}
.export-overlay-card {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #ffffff;
  padding: 16px 28px;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
}
.export-overlay-icon {
  font-size: 1.4rem;
  color: #6366f1;
}
.export-overlay-text {
  font-size: 0.95rem;
  font-weight: 600;
  color: #1e293b;
}
:global(.dark) .export-overlay-card { background: #1e293b; }
:global(.dark) .export-overlay-text { color: #e2e8f0; }
</style>
